import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, CalendarRange } from 'lucide-react';
import type { SeasonToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { formatDate } from '~/lib/format-date';
import { serverMessage } from '~/lib/api/server-error';
import { useSeasonsList, useSeasonMutations } from '~/hooks/use-seasons';
import { tokenToForm, type SeasonFormValues } from '~/lib/schemas/season';
import { SeasonEditDialog } from './season-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Seasons (Σεζόν) CRUD surface — parity with the legacy admin grid (Code/Name/Start/End/Visible/Sort). */
export function SeasonsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useSeasonsList();
	const { remove } = useSeasonMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<SeasonFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<SeasonToken | null>(null);

	const columns = useMemo<SystemGridColumn<SeasonToken>[]>(
		() => [
			{
				key: 'code',
				header: t('catalog.seasons.columns.code'),
				width: '180px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'name',
				header: t('catalog.seasons.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'startDate',
				header: t('catalog.seasons.columns.startDate'),
				width: '150px',
				sortValue: o => (o.sdt as string | null) ?? '',
				exportValue: o => formatDate(o.sdt as string | null),
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{(o.sdt && formatDate(o.sdt as string)) || '—'}</span>
			},
			{
				key: 'endDate',
				header: t('catalog.seasons.columns.endDate'),
				width: '150px',
				sortValue: o => (o.edt as string | null) ?? '',
				exportValue: o => formatDate(o.edt as string | null),
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{(o.edt && formatDate(o.edt as string)) || '—'}</span>
			},
			{
				key: 'visible',
				header: t('catalog.seasons.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.seasons.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<SeasonToken>
				gridId="seasons"
				title={t('catalog.seasons.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<CalendarRange size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.seasons.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="seasons"
			/>

			<SeasonEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.seasons.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.seasons.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
